import { Box, Button, Center, Flex, Text } from '@chakra-ui/react';

import { Trans } from '@lingui/react/macro';
import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { SortOrder } from '@duke-hq/lib/pagination';
import type { DateRangeKey } from '@duke-hq/svc-api/provider/trpc/routers/tenant/groupRewards/utils';
import { SiteLeaderboardTable } from './SiteLeaderboardTable';
import { DateRangeFilterButtons } from '../DateRangeFilterButtons';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_ORDER_BY = 'createdAt';

export type SortFilters = {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  dateRange: DateRangeKey | undefined;
};

function isDateRangeKey(value: unknown): value is DateRangeKey {
  return value === 'currentMonth' || value === 'lastMonth' || value === 'last6Months';
}

function useSiteLeaderboard(filters: SortFilters, currentLimit: number) {
  const queryResult =
    trpcTenant.groupRewards.listSiteLeaderboardWithInfiniteScroll.useInfiniteQuery(
      { filters, limit: currentLimit },
      {
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        placeholderData: (prev) => prev,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    );

  const sites = useMemo(
    () => queryResult.data?.pages.flatMap((page) => page.items) ?? [],
    [queryResult.data],
  );

  return {
    ...queryResult,
    sites,
  };
}

function updateFilters(prevFilters: SortFilters, newFilters: Partial<SortFilters>): SortFilters {
  return {
    ...prevFilters,
    ...newFilters,
    orderBy: newFilters.orderBy ?? prevFilters.orderBy ?? DEFAULT_ORDER_BY,
    sort: newFilters.sort ?? prevFilters.sort ?? SortOrder.DESC,
    dateRange:
      'dateRange' in newFilters
        ? isDateRangeKey(newFilters.dateRange)
          ? newFilters.dateRange
          : undefined
        : prevFilters.dateRange,
  };
}

export function SiteLeaderboard() {
  const [filters, setFilters] = useState<SortFilters>({
    orderBy: DEFAULT_ORDER_BY,
    sort: SortOrder.DESC,
    dateRange: undefined,
  });

  const [currentLimit, setCurrentLimit] = useState(DEFAULT_PAGE_SIZE);
  const maxLoadedItemsRef = useRef(DEFAULT_PAGE_SIZE);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const previousCountRef = useRef(0);

  const { sites, fetchNextPage, hasNextPage, isFetchingNextPage, error } = useSiteLeaderboard(
    filters,
    currentLimit,
  );

  const maxLoadedItems = Math.max(maxLoadedItemsRef.current, sites.length);
  maxLoadedItemsRef.current = maxLoadedItems;

  // Maintain scroll position when loading more
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (
      sites.length > previousCountRef.current &&
      previousCountRef.current > 0 &&
      tableScrollRef.current != null
    ) {
      const scrollContainer = tableScrollRef.current;

      // Always scroll to bottom when loading more records
      timeoutId = setTimeout(() => {
        if (scrollContainer != null) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
      }, 100);
    }

    previousCountRef.current = sites.length;

    return () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
    };
  }, [sites.length]);

  const handleFilterChange = useCallback(
    (newFilters: Partial<SortFilters>) => {
      setCurrentLimit(maxLoadedItems);
      setFilters((prevFilters) => updateFilters(prevFilters, newFilters));
    },
    [maxLoadedItems],
  );

  const handleLoadMore = useCallback(() => {
    setCurrentLimit((prev) => prev + DEFAULT_PAGE_SIZE);
    void fetchNextPage();
  }, [fetchNextPage]);

  if (error) {
    return (
      <Center p={8}>
        <Text color="red.500" textAlign="center">
          <Trans>Error loading data. Please try again.</Trans>
        </Text>
      </Center>
    );
  }

  return (
    <>
      <TableViewLayout
        tableHeight="auto"
        tableBgColor="bg.subtle"
        pageHeader={[
          <Text key="heading" textStyle="2xl" fontWeight="medium">
            <Trans>Top Sites</Trans>
          </Text>,
        ]}
        filters={
          <Flex w="full" justify="flex-end">
            <DateRangeFilterButtons filters={filters} onFilterChange={handleFilterChange} />
          </Flex>
        }
        table={
          <Box>
            {sites.length > 0 ? (
              <Box position="relative">
                <Box ref={tableScrollRef} maxHeight="600px" overflowY="auto" borderRadius="md">
                  <SiteLeaderboardTable
                    sites={sites}
                    filters={filters}
                    onFilters={handleFilterChange}
                  />
                </Box>
              </Box>
            ) : (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No sites found</Trans>
                </Text>
              </Center>
            )}
          </Box>
        }
      />
      {hasNextPage ? (
        <Flex justify="center" my={4}>
          <Button
            onClick={handleLoadMore}
            isLoading={isFetchingNextPage}
            loadingText="Loading more..."
            variant="link"
            size="xs"
            colorScheme="teal"
          >
            <Trans>Load More ({sites.length} loaded)</Trans>
          </Button>
        </Flex>
      ) : null}
    </>
  );
}
